<template>
<m-pageframe
    title="Separate Customers and Prospects"
>
<v-container
    fluid
    class="pa-4 pa-sm-6"
    :class="$vuetify.breakpoint.mdAndUp ? 'max-width-1200' : ''"
>
    <!-- Page Header -->
    <div class="mb-4">
        <h1 class="text-h4 font-weight-medium mb-2">Separate Customers and Prospects</h1>
        <p class="text-subtitle-1 grey--text text--darken-1">
            Upload an Excel file containing a worksheet with one or more email columns.
            The system will separate the data into customers (active or archived RedChirp users)
            and prospects (non-users) based on email addresses and domains.
        </p>
    </div>

    <!-- How It Works Section -->
    <v-card class="mb-4" outlined>
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-information</v-icon>
            How It Works
        </v-card-title>
        <v-card-text class="pt-3 pb-3">
            <v-row dense>
                <!-- Input Requirements -->
                <v-col cols="12" lg="4">
                    <div class="mb-3">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" size="20" class="mr-2">mdi-file-upload</v-icon>
                            <span class="subtitle-2 font-weight-medium">Input Requirements</span>
                        </div>
                        <v-list dense class="pa-0">
                            <v-list-item class="px-0 py-1">
                                <v-list-item-icon class="mr-2 my-0">
                                    <v-icon color="success" size="16">mdi-check-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="body-2">Excel file with email column(s)</v-list-item-title>
                                    <v-list-item-subtitle class="caption">Column name must contain "email" in it</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item class="px-0 py-1">
                                <v-list-item-icon class="mr-2 my-0">
                                    <v-icon color="success" size="16">mdi-check-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="body-2">.xlsx file format</v-list-item-title>
                                </v-list-item-content>
                                
                            </v-list-item>
                            <v-list-item class="px-0 py-1">
                                <v-list-item-icon class="mr-2 my-0">
                                    <v-icon color="success" size="16">mdi-check-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="body-2">max 100MB file, max 10k rows in sheet</v-list-item-title>
                                </v-list-item-content>
                                
                            </v-list-item>
                        </v-list>
                    </div>
                </v-col>

                <!-- Output Information -->
                <v-col cols="12" lg="4">
                    <div class="mb-3">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" size="20" class="mr-2">mdi-file-download</v-icon>
                            <span class="subtitle-2 font-weight-medium">Output</span>
                        </div>
                        <v-list dense class="pa-0">
                            <v-list-item class="px-0 py-1">
                                <v-list-item-icon class="mr-2 my-0">
                                    <v-icon color="primary" size="16">mdi-account-group</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="body-2">Customers worksheet</v-list-item-title>
                                    <v-list-item-subtitle class="caption">+ separation memo, web platfom, & crawl time columns</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item class="px-0 py-1">
                                <v-list-item-icon class="mr-2 my-0">
                                    <v-icon color="secondary" size="16">mdi-account-plus</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="body-2">Prospects worksheet</v-list-item-title>
                                    <v-list-item-subtitle class="caption">+ web platform & crawl time columns</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </div>
                </v-col>

                <!-- Customer Identification -->
                <v-col cols="12" lg="4">
                    <div class="mb-3">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" size="20" class="mr-2">mdi-account-search</v-icon>
                            <span class="subtitle-2 font-weight-medium">Customer Identification</span>
                        </div>
                        <v-list dense class="pa-0">
                            <v-list-item class="px-0 py-1">
                                <v-list-item-icon class="mr-2 my-0">
                                    <v-icon color="success" size="16">mdi-email-check</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="body-2">Email matches RedChirp username</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item class="px-0 py-1">
                                <v-list-item-icon class="mr-2 my-0">
                                    <v-icon color="success" size="16">mdi-domain</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="body-2">Domain matches RedChirp custom domain</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </div>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>

    <!-- Supported Email Formats Section -->
    <v-card class="mb-4" outlined>
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-email-variant</v-icon>
            Supported Email Formats
        </v-card-title>
        <v-card-text class="pt-3 pb-3">
            <p class="text-body-2 grey--text text--darken-1 mb-3">
                The system can process various email formats in your Excel columns:
            </p>
            <v-row dense>
                <v-col cols="12" md="4">
                    <div class="mb-3">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" size="20" class="mr-2">mdi-email</v-icon>
                            <span class="subtitle-2 font-weight-medium">Simple Emails</span>
                        </div>
                        <v-card flat color="grey lighten-5" class="pa-2">
                            <code class="text-body-2">user@domain.com</code>
                        </v-card>
                    </div>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="mb-3">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" size="20" class="mr-2">mdi-account-circle</v-icon>
                            <span class="subtitle-2 font-weight-medium">Display Names</span>
                        </div>
                        <v-card flat color="grey lighten-5" class="pa-2">
                            <code class="text-body-2">Jim Kane &lt;jim.kane@gmail.com&gt;</code>
                        </v-card>
                    </div>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="mb-3">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" size="20" class="mr-2">mdi-format-list-bulleted</v-icon>
                            <span class="subtitle-2 font-weight-medium">Multiple Emails</span>
                        </div>
                        <v-card flat color="grey lighten-5" class="pa-2">
                            <code class="text-body-2">user1@domain.com, user2@domain.com</code>
                            <br>
                            <code class="text-body-2">user1@domain.com; user2@domain.com</code>
                        </v-card>
                    </div>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>

    <!-- Upload File Section -->
    <v-card class="mb-4" outlined>
        <v-card-title class="grey lighten-4">
            <v-icon left color="primary">mdi-cloud-upload</v-icon>
            Upload Excel File
        </v-card-title>
        <v-card-text class="pt-3">
            <p class="text-body-2 grey--text text--darken-1 mb-3">
                Select an .xlsx file containing email addresses to process
            </p>

            <v-file-input
                v-model="selected_file"
                label="Select Excel File"
                accept=".xlsx"
                prepend-icon="mdi-file-excel"
                :disabled="processing"
                @change="onFileSelected"
                outlined
                hide-details="auto"
                class="mb-3"
            />

            <div v-if="selected_file" class="mb-3">
                <v-chip color="success" text-color="white" class="mr-2">
                    <v-icon left small>mdi-file-excel</v-icon>
                    {{ selected_file.name }}
                </v-chip>
                <span class="caption grey--text">{{ formatFileSize(selected_file.size) }}</span>
            </div>

            <v-fade-transition>
                <v-alert
                    v-if="file_error"
                    type="error"
                    class="mb-3"
                    dense
                    outlined
                >
                    {{ file_error }}
                </v-alert>
            </v-fade-transition>

            <v-fade-transition>
                <v-alert
                    v-if="file_success && !selected_file"
                    type="success"
                    class="mb-3"
                    dense
                    outlined
                >
                    {{ file_success }}
                </v-alert>
            </v-fade-transition>

            <!-- Sheet Selection -->
            <v-fade-transition>
                <div v-if="show_sheet_selection" class="mt-4">
                    <v-divider class="mb-4"></v-divider>
                    <div class="d-flex align-center mb-3">
                        <v-icon color="primary" class="mr-2">mdi-table</v-icon>
                        <span class="subtitle-2 font-weight-medium">Select Sheet to Process</span>
                    </div>
                    <p class="text-body-2 grey--text text--darken-1 mb-3">
                        The selected sheet will be processed for customer/prospect separation
                    </p>
                    <v-select
                        v-model="selected_sheet"
                        :items="available_sheets"
                        label="Choose worksheet"
                        prepend-icon="mdi-table"
                        :disabled="processing"
                        outlined
                        hide-details
                    />
                </div>
            </v-fade-transition>

            <div class="d-flex justify-end mt-4">
                <v-btn
                    v-if="!show_sheet_selection"
                    color="primary"
                    :disabled="!selected_file || processing"
                    @click="onProcessFile"
                    large
                >
                    <v-icon left>mdi-upload</v-icon>
                    Upload File
                </v-btn>
                <v-btn
                    v-if="show_sheet_selection"
                    color="primary"
                    :disabled="!selected_sheet || processing"
                    @click="onProcessWithSheet"
                    large
                >
                    <v-icon left>mdi-cog</v-icon>
                    Process Selected Sheet
                </v-btn>
            </div>
        </v-card-text>
    </v-card>

    <!-- Processing Status Section -->
    <v-fade-transition>
        <v-card
            v-if="processing_status"
            class="mb-4"
            outlined
        >
            <v-card-title class="grey lighten-4">
                <v-icon
                    left
                    :color="processing_status.error ? 'error' : processing ? 'warning' : 'success'"
                >
                    {{ processing_status.error ? 'mdi-alert-circle' : processing ? 'mdi-cog' : 'mdi-check-circle' }}
                </v-icon>
                {{ processing ? 'Processing File...' : processing_status.error ? 'Processing Failed' : 'Processing Complete' }}
            </v-card-title>
            <v-card-text class="pt-4">
                <p class="text-body-2 grey--text text--darken-1 mb-4">
                    {{ processing ? 'Analyzing email addresses and separating data' : processing_status.error ? 'An error occurred during processing' : 'File has been successfully processed' }}
                </p>

                <!-- Phase-based Progress Tracking
                     Shows progress across 4 phases:
                     1. Data Analysis (5%) - Quick analysis of source data
                     2. Organization Lookup (10%) - Looking up organizations for domains
                     3. Platform Detection (70%) - BuiltWith API calls (most time-consuming)
                     4. Report Generation (15%) - Creating final Excel report
                -->
                <div v-if="processing" class="mb-4">
                    <!-- Overall Progress -->
                    <div class="mb-4">
                        <div class="d-flex justify-space-between align-center mb-2">
                            <span class="text-body-1 font-weight-medium">Overall Progress</span>
                            <span class="text-body-2 grey--text">{{ Math.round(overall_progress_percentage) }}% Complete</span>
                        </div>
                        <v-progress-linear
                            :value="overall_progress_percentage"
                            color="success"
                            height="10"
                            rounded
                        />
                    </div>

                    <!-- Current Phase Progress -->
                    <div class="mb-4">
                        <div class="d-flex justify-space-between align-center mb-2">
                            <span class="text-body-2">
                                <v-icon small class="mr-1" :color="current_phase_info.color">{{ current_phase_info.icon }}</v-icon>
                                {{ current_phase_info.name }}
                            </span>
                            <span class="text-body-2 grey--text">{{ current_phase_info.detail }}</span>
                        </div>
                        <v-progress-linear
                            :indeterminate="current_phase_info.indeterminate"
                            :value="current_phase_progress_percentage"
                            :color="current_phase_info.color"
                            height="6"
                            rounded
                        />
                    </div>

                    <!-- Phase Steps Overview -->
                    <div class="mb-4">
                        <v-row no-gutters>
                            <v-col v-for="(phase, index) in phase_steps" :key="index" :cols="phase.weight * 12 / 100">
                                <div class="text-center pa-1">
                                    <v-icon
                                        small
                                        :color="phase.completed ? 'success' : phase.active ? phase.color : 'grey lighten-1'"
                                    >
                                        {{ phase.completed ? 'mdi-check-circle' : phase.icon }}
                                    </v-icon>
                                    <div class="text-caption mt-1" :class="phase.active ? 'font-weight-bold' : 'grey--text'">
                                        {{ phase.short_name }}
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Fun Animal Fact while processing -->
                    <div v-if="current_animal_fact" class="mt-3 pa-3 blue-grey lighten-5 rounded">
                        <div class="d-flex align-center">
                            <v-icon color="primary" class="mr-2">mdi-paw</v-icon>
                            <span class="text-body-2 font-weight-medium">Did you know?</span>
                        </div>
                        <p class="text-body-2 mb-0 mt-1">{{ current_animal_fact }}</p>
                    </div>
                </div>

                <!-- Results Summary -->
                <div v-if="processing_status && processing_status.total_rows && !processing_status.error">
                    <v-row class="mb-4">
                        <v-col cols="12" sm="4">
                            <v-card outlined class="text-center pa-3">
                                <v-icon color="info" size="32" class="mb-2">mdi-file-table</v-icon>
                                <div class="text-h6 font-weight-medium">{{ processing_status.total_rows }}</div>
                                <div class="caption grey--text">Total Rows</div>
                            </v-card>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-card outlined class="text-center pa-3">
                                <v-icon color="primary" size="32" class="mb-2">mdi-account-group</v-icon>
                                <div class="text-h6 font-weight-medium">{{ processing_status.customer_count }}</div>
                                <div class="caption grey--text">Customers</div>
                            </v-card>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-card outlined class="text-center pa-3">
                                <v-icon color="secondary" size="32" class="mb-2">mdi-account-plus</v-icon>
                                <div class="text-h6 font-weight-medium">{{ processing_status.prospect_count }}</div>
                                <div class="caption grey--text">Prospects</div>
                            </v-card>
                        </v-col>
                    </v-row>
                </div>

                <!-- Error Alert -->
                <v-fade-transition>
                    <v-alert
                        v-if="processing_status && processing_status.error"
                        type="error"
                        outlined
                        dense
                        class="mb-4"
                    >
                        {{ processing_status.error }}
                    </v-alert>
                </v-fade-transition>

                <!-- Download Action -->
                <div v-if="processing_status && processing_status.download_url" class="d-flex justify-end">
                    <v-btn
                        :href="processing_status.download_url"
                        download
                        color="primary"
                        large
                    >
                        <v-icon left>mdi-download</v-icon>
                        Download Separated File
                    </v-btn>
                </div>
            </v-card-text>
        </v-card>
    </v-fade-transition>

</v-container>
</m-pageframe>
</template>

<script>
export default {
    data() {
        return {
            selected_file: null,
            processing: false,
            file_error: null,
            file_success: null,
            processing_status: null,
            uploaded_blob_id: null,
            available_sheets: [],
            selected_sheet: null,
            show_sheet_selection: false,

            // Progress tracking
            progress_status: null,
            progress_counter: 0,
            progress_percentage: 0,
            estimated_total_rows: 0,
            process_id: null,
            progress_interval: null,
            current_animal_fact: '',
            animal_fact_interval: null,

            // Phase-based progress tracking
            current_phase: null,
            phase_data: {
                domains_to_process: 0,
                customers_to_enhance: 0,
                prospects_to_enhance: 0,
                rows_to_classify: 0,
                usernames_to_process: 0,
                phase_progress_counter: 0
            },

            animal_facts: [
                "🐙 Octopuses have three hearts and blue blood!",
                "🦒 A giraffe's tongue is 18-20 inches long and dark purple to prevent sunburn.",
                "🐧 Penguins propose to their mates with pebbles.",
                "🦘 Kangaroos can't walk backwards due to their large tail and powerful hind legs.",
                "🐨 Koalas sleep 18-22 hours per day and have fingerprints similar to humans.",
                "🦆 Ducks have waterproof feathers and can sleep with one eye open.",
                "🐘 Elephants can recognize themselves in mirrors and mourn their dead.",
                "🦋 Butterflies taste with their feet and smell with their antennae.",
                "🐬 Dolphins have names for each other and can recognize themselves in mirrors.",
                "🦉 Owls can rotate their heads 270 degrees and have asymmetrical ear holes.",
                "🐝 Bees dance to communicate the location of flowers to their hive mates.",
                "🦎 Geckos can walk on ceilings thanks to millions of tiny hairs on their feet.",
                "🐠 Clownfish are all born male and can change to female when needed.",
                "🦅 Eagles can see 4-8 times better than humans and spot prey from 2 miles away.",
                "🐺 Wolves have an incredible sense of smell 100 times stronger than humans.",
                "🦏 Rhino horns are made of keratin, the same material as human hair and nails.",
                "🐙 Octopuses are incredibly intelligent and can solve puzzles and use tools.",
                "🦜 Parrots can live over 100 years and some can learn over 100 words.",
                "🐢 Sea turtles use Earth's magnetic field to navigate across oceans.",
                "🦔 Hedgehogs have around 5,000 spines that they can raise when threatened.",
                "🐸 Frogs absorb water through their skin instead of drinking it.",
                "🦇 Bats are the only mammals capable of sustained flight.",
                "🐧 Emperor penguins can dive to depths of 1,800 feet and hold their breath for 22 minutes.",
                "🦌 Reindeer eyes change color from gold in summer to blue in winter.",
                "🐻 Polar bears have black skin under their white fur to absorb heat.",
                "🦊 Foxes use Earth's magnetic field to hunt and can hear prey under snow.",
                "🐳 Blue whales' hearts are so large that a small child could crawl through their arteries.",
                "🦘 Baby kangaroos are only 2 cm long when born and crawl into their mother's pouch.",
                "🐨 Koalas rarely drink water - they get moisture from eucalyptus leaves.",
                "🦆 Ducklings imprint on the first moving object they see after hatching.",
                "🐘 Elephants are afraid of bees and will avoid areas where they hear buzzing.",
                "🦋 Monarch butterflies migrate up to 3,000 miles using a built-in compass.",
                "🐬 Dolphins give themselves names with unique whistle signatures.",
                "🦉 Barn owls can hunt in complete darkness using only their hearing.",
                "🐝 A single bee colony can produce 60-100 pounds of honey per year.",
                "🦎 Chameleons can move their eyes independently and see in two directions at once.",
                "🐠 Seahorses are the only species where males get pregnant and give birth.",
                "🦅 Peregrine falcons are the fastest animals on Earth, diving at 240+ mph.",
                "🐺 Wolf packs are actually family units led by the parents, not an 'alpha'.",
                "🦏 White rhinos aren't actually white - the name comes from 'wide' referring to their lips.",
                "🐙 Octopuses have no bones and can squeeze through any opening larger than their beak.",
                "🦜 African grey parrots can learn to use words in context and understand concepts.",
                "🐢 Some tortoises can live over 150 years and outlive multiple human generations.",
                "🦔 Hedgehogs are lactose intolerant despite popular belief about milk.",
                "🐸 Some frogs can freeze solid in winter and thaw out alive in spring.",
                "🦇 Vampire bats share blood with hungry colony members through regurgitation.",
                "🐧 Adelie penguins build nests out of stones and steal stones from neighbors.",
                "🦌 Deer can jump up to 10 feet high and run up to 40 mph.",
                "🐻 Bears can run up to 35 mph - faster than any human.",
                "🦊 Arctic foxes can survive temperatures as low as -58°F thanks to their fur.",
                "🐅 Tigers have striped skin, not just striped fur, and each pattern is unique like a fingerprint.",
                "🐍 Some snakes can 'fly' by flattening their bodies and gliding from tree to tree.",
                "🐒 Monkeys have distinct vocalizations for different predators, warning others of specific threats.",
                "🐼 Giant pandas eat up to 12-38 pounds of bamboo every day and spend 10-16 hours eating.",
                "🦨 Skunks can spray their noxious liquid up to 10 feet with remarkable accuracy.",
                "🦡 Badgers are strong diggers and create extensive underground tunnel systems called setts.",
                "🐿️ Squirrels plant thousands of new trees each year by forgetting where they buried their acorns.",
                "🦔 Moles are practically blind but have an excellent sense of touch and smell to find food.",
                "🐟 Some fish, like the great white shark, never stop swimming, even when they're sleeping, to keep water flowing over their gills.",
                "🕷️ Spiders are not insects; they are arachnids and have eight legs.",
                "🐜 Army ants form living bridges with their bodies to cross gaps and overcome obstacles.",
                "🐞 Ladybugs are considered good luck and can eat up to 5,000 aphids in their lifetime.",
                "🦗 Crickets 'sing' by rubbing their wings together to attract mates.",
                "🐌 Snails can sleep for up to three years, waiting for the right conditions.",
                "🦀 Some crabs can regrow lost limbs and even eyes.",
                "🦈 Sharks have been around for over 400 million years, predating dinosaurs.",
                "🐊 Alligators have U-shaped snouts, while crocodiles have more pointed V-shaped snouts.",
                "🐢 The oldest known living land animal is a tortoise named Jonathan, born around 1832.",
                "🐦 Hummingbirds are the only birds that can fly backward.",
                "🦉 Snowy owls have such good hearing that they can locate prey under a foot of snow.",
                "🦅 Bald eagles can live for 20-30 years in the wild.",
                "🦩 Flamingos get their pink color from the beta-carotene in their diet of brine shrimp and algae.",
                "🐐 Goats have rectangular pupils, which gives them a panoramic view and helps them spot predators.",
                "🐑 Sheep have excellent memories and can remember at least 50 individual sheep and humans for years.",
                "🐖 Pigs are highly intelligent animals and can be taught to play video games.",
                "🐎 Horses can sleep both standing up and lying down.",
                "🐈 Domestic cats can make over 100 different sounds, while dogs can only make about 10.",
                "🐕 A dog's nose print is unique, much like a human's fingerprint, and can be used for identification.",
                "🐄 Cows have best friends and get stressed when separated from them.",
                "🐓 Chickens are the closest living relatives of the Tyrannosaurus Rex.",
                "🐝 Worker bees are all female and do all the foraging, hive building, and nursing.",
                "🐜 Bullet ants have the most painful sting of any insect, comparable to being shot.",
                "🦘 Wallabies are smaller relatives of kangaroos and are native to Australia and New Guinea.",
                "🐒 Howler monkeys are the loudest land animals, with their calls audible from up to 3 miles away."
            ]
        }
    },

    computed: {
        // Define the processing phases with their weights and characteristics
        phase_steps() {
            return [
                {
                    key: 'analysis',
                    name: 'Data Analysis',
                    short_name: 'Analysis',
                    icon: 'mdi-file-search',
                    color: 'blue',
                    weight: 5,
                    completed: this.isPhaseCompleted('analysis'),
                    active: this.current_phase === 'analysis' || (!this.current_phase && this.progress_counter === 0)
                },
                {
                    key: 'organizations',
                    name: 'Looking up organizations',
                    short_name: 'Organizations',
                    icon: 'mdi-domain',
                    color: 'orange',
                    weight: 10,
                    completed: this.isPhaseCompleted('organizations'),
                    active: this.current_phase === 'Looking up organizations'
                },
                {
                    key: 'platforms',
                    name: 'Detecting website platforms',
                    short_name: 'Platforms',
                    icon: 'mdi-web',
                    color: 'purple',
                    weight: 70,
                    completed: this.isPhaseCompleted('platforms'),
                    active: this.current_phase === 'Detecting website platforms'
                },
                {
                    key: 'report',
                    name: 'Generating final report',
                    short_name: 'Report',
                    icon: 'mdi-file-document',
                    color: 'green',
                    weight: 15,
                    completed: this.isPhaseCompleted('report'),
                    active: this.current_phase === 'Generating final report'
                }
            ];
        },

        // Get information about the current active phase
        current_phase_info() {
            const active_phase = this.phase_steps.find(phase => phase.active);
            if (!active_phase) {
                return {
                    name: 'Starting process...',
                    icon: 'mdi-play-circle',
                    color: 'primary',
                    detail: '',
                    indeterminate: true
                };
            }

            let detail = '';
            let indeterminate = false;

            // Calculate phase-specific details
            if (active_phase.key === 'organizations' && this.phase_data.usernames_to_process > 0) {
                const usernames_processed = this.phase_data.phase_progress_counter !== undefined ?
                    this.phase_data.phase_progress_counter : 0;
                detail = `${usernames_processed} of ${this.phase_data.usernames_to_process} usernames`;
            } else if (active_phase.key === 'platforms' && this.phase_data.domains_to_process > 0) {
                const domains_processed = this.phase_data.phase_progress_counter !== undefined ?
                    this.phase_data.phase_progress_counter : 0;
                detail = `${domains_processed} of ${this.phase_data.domains_to_process} domains`;
            } else if (active_phase.key === 'report' && this.phase_data.rows_to_classify > 0) {
                const rows_processed = this.phase_data.phase_progress_counter !== undefined ?
                    this.phase_data.phase_progress_counter : 0;
                detail = `${rows_processed} of ${this.phase_data.rows_to_classify} rows`;
            } else if (this.progress_counter > 0) {
                detail = `${this.progress_counter} processed`;
            } else {
                indeterminate = true;
            }

            return {
                name: active_phase.name,
                icon: active_phase.icon,
                color: active_phase.color,
                detail: detail,
                indeterminate: indeterminate
            };
        },

        // Calculate progress percentage for the current phase
        current_phase_progress_percentage() {
            const active_phase = this.phase_steps.find(phase => phase.active);
            if (!active_phase) return 0;

            const counter = this.phase_data.phase_progress_counter !== undefined ?
                this.phase_data.phase_progress_counter : 0;

            if (active_phase.key === 'organizations' && this.phase_data.usernames_to_process > 0) {
                return Math.min(95, (counter / this.phase_data.usernames_to_process) * 100);
            } else if (active_phase.key === 'platforms' && this.phase_data.domains_to_process > 0) {
                return Math.min(95, (counter / this.phase_data.domains_to_process) * 100);
            } else if (active_phase.key === 'report' && this.phase_data.rows_to_classify > 0) {
                return Math.min(95, (counter / this.phase_data.rows_to_classify) * 100);
            } else if (counter > 0 || this.progress_counter > 0) {
                return Math.min(95, 50); // Default progress for other phases
            }

            return 10; // Minimal progress to show activity
        },

        // Calculate overall progress across all phases
        overall_progress_percentage() {
            let total_progress = 0;

            for (const phase of this.phase_steps) {
                if (phase.completed) {
                    total_progress += phase.weight;
                } else if (phase.active) {
                    total_progress += (this.current_phase_progress_percentage / 100) * phase.weight;
                    break; // Don't count future phases
                }
            }

            return Math.min(99, total_progress);
        }
    },

    methods: {
        onFileSelected() {
            this.file_error = null;
            this.file_success = null;
            this.processing_status = null;
            this.show_sheet_selection = false;
            this.available_sheets = [];
            this.selected_sheet = null;
            this.uploaded_blob_id = null;
            
            if (this.selected_file) {
                // Validate file type
                const allowed_types = [
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' // This is for .xlsx files
                ];

                if (!allowed_types.includes(this.selected_file.type)) {
                    this.file_error = 'Please select a valid Excel file (.xlsx).'; 
                    this.selected_file = null;
                    return;
                }

                // Validate file size (100MB limit)
                const max_size = 100 * 1024 * 1024;
                if (this.selected_file.size > max_size) {
                    this.file_error = 'File size must be less than 100MB.';
                    this.selected_file = null;
                    return;
                }

                this.file_success = `Selected: ${this.selected_file.name} (${this.formatFileSize(this.selected_file.size)})`;
            }
        },

        onProcessFile() {
            if (!this.selected_file) {
                this.file_error = 'Please select a file first';
                return;
            }

            this.processing = true;
            this.file_error = null;
            // Don't set processing_status here - only set it when actual processing begins

            // Step 1: Upload the file
            this.uploadFile();
        },

        uploadFile() {
            // Create form data for file upload
            const form_data = new FormData();
            form_data.append('excel_file', this.selected_file);
            form_data.append('label', this.selected_file.name);

            this.$rc.makeApiCall({
                url: 'admin/upload-customer-prospect-file',
                method: 'post',
                form_data: form_data,
                timeout: 10 * 60 * 1000, // 10 minutes for upload
                on_response: 'onResponseStandard',
                on_response_params: {
                    on_success: this.onUploadSuccess,
                    on_error: this.onProcessError
                }
            });
        },

        onUploadSuccess(response) {

            // Store the blob ID and sheet names
            this.uploaded_blob_id = response.blob_id;
            this.available_sheets = response.sheet_names || [];

            if (this.available_sheets.length === 0) {
                this.processing = false;
                this.file_error = 'No sheets found in the Excel file';
                return;
            }

            // If only one sheet, auto-select it and process immediately
            if (this.available_sheets.length === 1) {
                this.selected_sheet = this.available_sheets[0];
                this.processWithSelectedSheet();
            } else {
                // Show sheet selection UI
                this.processing = false;
                this.show_sheet_selection = true;
                this.selected_sheet = this.available_sheets[0]; // Default to first sheet
            }
        },

        onProcessWithSheet() {
            if (!this.selected_sheet) {
                this.file_error = 'Please select a sheet to process';
                return;
            }

            this.file_error = null;
            this.processWithSelectedSheet();
        },

        processWithSelectedSheet() {
            // Clear any previous results and hide sheet selection
            this.show_sheet_selection = false;
            this.processing = true;
            this.progress_status = 'Starting process...';
            this.progress_counter = 0;
            this.progress_percentage = 0;
            this.estimated_total_rows = 0;

            // Set initial processing status to show the progress section
            this.processing_status = {
                total_rows: 0,
                customer_count: 0,
                prospect_count: 0,
                error: null,
                download_url: null
            };

            // Start the async process
            const request_body = {
                blob_id: this.uploaded_blob_id,
                sheet_name: this.selected_sheet
            };

            this.$rc.makeApiCall({
                url: 'employees/separate-customers-prospects-async',
                method: 'post',
                data: request_body,
                on_response: 'onResponseStandard',
                on_response_params: {
                    on_success: this.onProcessStarted,
                    on_error: this.onProcessError
                }
            });
        },

        onProcessStarted(response) {
            this.process_id = response.process_id;
            this.progress_status = 'Process started, checking progress...';

            // Update URL with process ID so users can bookmark/refresh
            this.updateUrlWithProcessId(this.process_id);

            // Start polling for progress
            this.startProgressPolling();
        },

        startProgressPolling() {
            this.progress_interval = setInterval(() => {
                this.checkProgress();
            }, 1000); // Check every second

            // Start showing animal facts
            this.startAnimalFacts();
        },

        checkProgress() {
            if (!this.process_id) return;

            this.$rc.makeApiCall({
                url: `process/${this.process_id}`,
                method: 'get',
                on_response: 'onResponseStandard',
                on_response_params: {
                    on_success: this.onProgressUpdate,
                    on_error: this.onProgressError
                }
            });
        },

        onProgressUpdate(pte) {
            this.progress_counter = pte.progress_counter || 0;

            // Update status based on process state
            if (pte.state === 'finished') {
                this.onProcessComplete(pte);
            } else if (pte.state === 'failed') {
                this.onProcessFailed(pte);
            } else if (pte.state === 'running') {
                this.updateRunningProgress(pte);
            } else {
                this.progress_status = 'Process queued...';
                this.progress_percentage = 0;
            }
        },

        updateRunningProgress(pte) {
            // Get status information
            const status = pte.status || {};

            // Update phase information from backend
            if (status.phase) {
                this.current_phase = status.phase;
            }

            // Update phase-specific data
            if (status.domains_to_process !== undefined) {
                this.phase_data.domains_to_process = status.domains_to_process;
            }
            if (status.customers_to_enhance !== undefined) {
                this.phase_data.customers_to_enhance = status.customers_to_enhance;
            }
            if (status.prospects_to_enhance !== undefined) {
                this.phase_data.prospects_to_enhance = status.prospects_to_enhance;
            }
            if (status.rows_to_classify !== undefined) {
                this.phase_data.rows_to_classify = status.rows_to_classify;
            }
            if (status.usernames_to_process !== undefined) {
                this.phase_data.usernames_to_process = status.usernames_to_process;
            }

            // Update phase-specific progress counter
            if (status.phase_progress_counter !== undefined) {
                this.phase_data.phase_progress_counter = status.phase_progress_counter;
            }

            // Use total_rows from status if available (for legacy compatibility)
            if (status.total_rows) {
                this.estimated_total_rows = status.total_rows;
            }

            // Update progress status for legacy display (kept for compatibility)
            if (this.current_phase) {
                this.progress_status = this.current_phase;
            } else if (this.progress_counter > 0) {
                this.progress_status = `Processing... (${this.progress_counter} processed)`;
            } else {
                this.progress_status = 'Starting to process...';
            }

            // Update legacy progress percentage (now calculated by computed properties)
            this.progress_percentage = this.overall_progress_percentage;
        },

        // Helper method to determine if a phase is completed
        isPhaseCompleted(phase_key) {
            if (!this.current_phase) return false;

            const phase_order = ['analysis', 'organizations', 'platforms', 'report'];
            const current_index = phase_order.findIndex(p => {
                if (p === 'organizations') return this.current_phase === 'Looking up organizations';
                if (p === 'platforms') return this.current_phase === 'Detecting website platforms';
                if (p === 'report') return this.current_phase === 'Generating final report';
                return false;
            });

            const target_index = phase_order.indexOf(phase_key);
            return current_index > target_index;
        },

        onProcessComplete(pte) {
            this.stopProgressPolling();
            this.processing = false;

            // Reset phase tracking
            this.current_phase = null;
            this.phase_data = {
                domains_to_process: 0,
                customers_to_enhance: 0,
                prospects_to_enhance: 0,
                rows_to_classify: 0,
                usernames_to_process: 0,
                phase_progress_counter: 0
            };

            if (pte.output) {
                this.processing_status = {
                    total_rows: pte.output.total_rows,
                    customer_count: pte.output.customer_count,
                    prospect_count: pte.output.prospect_count,
                    error: null,
                    download_url: pte.output.download_url
                };
                this.progress_status = `Complete! Processed ${pte.output.total_rows} rows.`;
                this.progress_percentage = 100;
            } else {
                this.onProcessFailed(pte);
            }
        },

        onProcessFailed(pte) {
            this.stopProgressPolling();
            this.processing = false;

            // Reset phase tracking
            this.current_phase = null;
            this.phase_data = {
                domains_to_process: 0,
                customers_to_enhance: 0,
                prospects_to_enhance: 0,
                rows_to_classify: 0,
                usernames_to_process: 0,
                phase_progress_counter: 0
            };

            this.processing_status = {
                total_rows: 0,
                customer_count: 0,
                prospect_count: 0,
                error: pte.exception || 'Process failed',
                download_url: null
            };
            this.progress_status = 'Process failed';
        },

        onProgressError() {
            // Don't stop polling immediately - the process might still be running
        },

        stopProgressPolling() {
            if (this.progress_interval) {
                clearInterval(this.progress_interval);
                this.progress_interval = null;
            }
            this.stopAnimalFacts();
        },

        startAnimalFacts() {
            // Show first fact immediately. This is highly important.
            this.showRandomAnimalFact();

            // Then rotate facts every 30 seconds
            this.animal_fact_interval = setInterval(() => {
                this.showRandomAnimalFact();
            }, 30000);
        },

        showRandomAnimalFact() {
            const random_index = Math.floor(Math.random() * this.animal_facts.length);
            this.current_animal_fact = this.animal_facts[random_index];
        },

        stopAnimalFacts() {
            if (this.animal_fact_interval) {
                clearInterval(this.animal_fact_interval);
                this.animal_fact_interval = null;
            }
            this.current_animal_fact = '';
        },

        onProcessError(error) {
            this.processing = false;
            this.processing_status = {
                total_rows: 0,
                customer_count: 0,
                prospect_count: 0,
                error: error.message || 'An error occurred while processing the file',
                download_url: null
            };
            this.stopProgressPolling();
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        updateUrlWithProcessId(processId) {
            // Update URL with process ID parameter
            const newUrl = new URL(window.location);
            newUrl.searchParams.set('process_id', processId);
            window.history.replaceState({}, '', newUrl);
        },

        checkForExistingProcess() {
            // Check if there's a process ID in the URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            const processId = urlParams.get('process_id');

            if (processId) {
                this.process_id = processId;
                this.processing = true; // Set processing flag to show in-progress UI
                this.progress_status = 'Checking existing process...';

                // Set initial processing status to show the progress section
                this.processing_status = {
                    total_rows: 0,
                    customer_count: 0,
                    prospect_count: 0,
                    error: null,
                    download_url: null
                };

                // Start polling for this existing process
                this.startProgressPolling();
            }
        }
    },

    mounted() {
        // Check if there's an existing process to track when component loads
        this.checkForExistingProcess();
    },

    beforeDestroy() {
        // Clean up progress polling and animal facts when component is destroyed
        this.stopProgressPolling();
        this.stopAnimalFacts();
    }
}
</script>

<style scoped>
/* Container max width */
.max-width-1200 {
    max-width: 1200px;
    margin: 0 auto;
}

/* Ensure consistent spacing */
.v-card {
    margin-bottom: 16px;
}

/* Better list styling */
.v-list-item__icon {
    margin-right: 12px !important;
    min-width: 32px !important;
}

/* Smooth transitions */
.v-btn {
    transition: all 0.3s ease;
}

/* Ensure proper spacing in nested cards */
.v-list-item {
    min-height: auto !important;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
}
</style>
